import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, catchError, filter, map, of, Subscription } from 'rxjs';
import moment from 'moment-timezone';
import { DurationInputArg2 } from 'moment-timezone';
import { includes, cloneDeep } from 'lodash';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../enums/report-block-type.enum';
import { DataPointModel } from '../../Charts/models/data-point.model';
import { chartColors } from '../../Charts/enums/chart-colors.enum';
import { ReportSeriesModel } from '../models/report-series.model';
import { mediaTypeToPieMap } from './reports-series-mapper.service';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { ReportDateRangeModel } from '../models/report-date-range.model';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { JournodexGeographicEntityModel } from '../../Journodex/models/journodex-geographic-entity.model';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { dateQueryInput24HoursOption, dateQueryInput30DaysOption } from '../../Forms/models/date-query-input-options.model';
import { SeriesTypeEnum } from '../enums/series-type.enum';
import { ExternalChartMetricsEnum } from '../enums/external-chart-metrics.enum';
import { ChartDisplayElementModel } from '../models/chart-display-element.model';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { AlertSurgeMetricEventsEnum, TargetingEventsEnum, userEventToIconMap } from '../enums/user_event.enum';
import { DistributionStatus } from '../../TargetingShared/enum/distribution-status.enum';
import { userAppPreferencesDefault } from '../../../Core/Users/data/user-app-preferences-default';
import { UserAppPreferenceChartColors } from '../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { PreferencesModel } from '../../../Core/Users/models/preferences.model';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { WordCloudSeriesModel } from '../../Charts/models/WordCloudChart/word-cloud-series.model';
import { TermTypesEnum } from '../enums/term-types.enum';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { MonitorConfigService } from '../../MonitorShared/services/monitor-config.service';
import { MapChartColorSchemeEnum } from '../../Charts/models/map-chart.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { RelativeTimezoneDateTypeEnum } from '../../../api/mentions/enum/relative-timezone-date-type.enum';
import { ContactApiModel, OutletApiModel } from '../../../api/targeting/model/targeting-search-result.api.model';
import { MediaTypeEnum, mediaTypeNoHeadlineMap, socialMediaTypeImageMap, mediaTypeIconMap } from '../../Basic/enums/media-type.enum';



export enum ReportsMockDataLabelTypesEnum {
    authorNames,
    outletNames,
    hashtags,
    socialMedia,
    referralSources,
    titles,
    words,
    handles
}

export const reportsMockDataLabelMap = new Map<ReportsMockDataLabelTypesEnum, string[]>([
    [
        ReportsMockDataLabelTypesEnum.authorNames,
        [
            'Robert Fisk',
            'Kate Adie',
            'Christiane Amanpour',
            'Hu Shuli',
            'Bob Woodward',
            'Anderson Cooper',
            'Louis Theroux',
            'Diane Sawyer',
            'Shereen Bhan',
            'Glenn Greenwald'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.outletNames,
        [
            'Horizon Times',
            'BBC News',
            'Wall Street Journal',
            'New York Times',
            'CNN',
            'Bloomberg',
            'CNET',
            'The Guardian',
            'The Independent',
            'Washington Post'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.hashtags,
        [
            '#mediarelations',
            '#publicspeaker',
            '#business',
            '#prtips',
            '#socialmediamarketing',
            '#prlife',
            '#publicrelationsagency',
            '#pragency',
            '#writer',
            '#socialmedia'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.socialMedia,
        [
            'X',
            'Facebook',
            'Instagram',
            'LinkedIn',
            'Pinterest',
            'YouTube',
            'Tumblr',
            'TikTok',
            'Reddit',
            'Bluesky'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.referralSources,
        [
            'google',
            'baidu',
            'sciencedaily.com',
            'yahooo.com',
            'agility.com',
            'tag24.com',
            'dognews.com',
            'topnews.com',
            'name123.com',
            'facebook.com'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.titles,
        [
            'Technology Canada',
            'Technology Ottawa 2021',
            'Technology',
            'Wearable Tools',
            'New Technology',
            'Trending Canada',
            'US Technology',
            'EV Canada',
            'Wearable Technology',
            'Europe Technology'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.words,
        [
            'impulse',
            'blame',
            'bomb',
            'feeling',
            'available',
            'philosophy',
            'bar',
            'paint',
            'program',
            'training'
        ]
    ],
    [
        ReportsMockDataLabelTypesEnum.handles,
        [
            '@Gameloft',
            '@RareLtd',
            '@CAGames',
            '@Supercell',
            '@C4Etech',
            '@LifeFit',
            '@newBorn',
            '@AgilityPR',
            '@CryptoMaster',
            '@CoinWinner'
        ]
    ]
]);

export const reportsMockAuthorData: ContactApiModel = {
    firstName: 'Robert',
    lastName: 'Fisk',
    employments: [
        {
            jobTitle: 'Journalist',
            outletInfo: {
                name: 'Global Daily News'
            }
        }
    ],
    bio: 'Robert Fisk is an experienced journalist with over a decade of reporting across digital and print platforms. ' +
        'Their coverage focuses on current affairs, technology, and cultural trends, bringing audiences timely insights and clear perspectives.'
};

export const reportsMockOutletData: OutletApiModel = {
    name: 'Horizon Times',
    mediaTypes: [{ enumValue: MediaTypeEnum.TV, displayString: '' }],
    languages: ['en'],
    bio: 'Horizon Times delivers comprehensive broadcast coverage of entertainment, sports, and cultural events. ' +
        'Known for dynamic programming and on-the-ground reporting, the station has become a go-to source for viewers seeking both news and lifestyle content.'
};


@Injectable()
export class ReportsSeriesPreviewService implements OnDestroy {

    private subscriptions: Subscription[] = [];
    private userChartColors: UserAppPreferenceChartColors;
    private preferences: PreferencesModel;
    private hasTiktok: boolean = false;

    /**
     * Class Constructor
     */
    constructor(
        public localizationService: LocalizationService,
        private journodexSearchService: JournodexRegionsService,
        private userPreferencesService: UserPreferencesService,
        private currentUserStateService: CurrentUserStateService,
        private monitorConfigService: MonitorConfigService
    ) {
        this.subscriptions.push(
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(userChartColors => {
                this.userChartColors = userChartColors;
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
            }),
            this.monitorConfigService.getAvailableMediaTypes().subscribe(mediaTypes => {
                this.hasTiktok = mediaTypes.includes(MediaTypeEnum.TIKTOK);
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions?.forEach(sub => sub.unsubscribe());
    }

    getPreviewSeries(type: ReportBlockTypeEnum, series: ReportSeriesModel[], displayElement?: ChartDisplayElementModel): ReportSeriesModel[] {
        if (!series || series.length < 1) {
            return [];
        }

        // if there's an issue, jus
        let previewSeries: ReportSeriesModel[];

        switch (type) {
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                previewSeries = series.map((seriesItem) => {
                    return this.previewMediaTypeBreakdownSeries(seriesItem, type);
                });
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                previewSeries = series.map((seriesItem) => {
                    return this.previewSentimentBreakdownSeries(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                previewSeries = this.previewSentimentTrend(series);
                break;
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
                previewSeries = series.map((seriesItem) => {
                    return this.previewTop10FacetTypeByVolumeSeries(seriesItem, reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.outletNames));
                });
                break;
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
                previewSeries = series.map((seriesItem) => {
                    return this.previewTop10FacetTypeByVolumeSeries(seriesItem, reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames));
                });
                break;
            case ReportBlockTypeEnum.TOP_HASHTAGS:
                previewSeries = series.map((seriesItem) => {
                    return this.previewTop10FacetTypeByVolumeSeries(seriesItem, reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.hashtags));
                });
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.RECENT_TOPICS:
                previewSeries = series.map((seriesItem) => {
                    return this.previewCoverageOverTimeSeries(seriesItem, 1, null, displayElement?.timeInterval);
                });
                break;
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                previewSeries = series.map((seriesItem, index) => {
                    return this.previewCoverageComparison(seriesItem, index);
                });
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                previewSeries = series.map((seriesItem) => {
                    return this.previewGeographyBreakdownByVolumeSeries(seriesItem, displayElement);
                });
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
                previewSeries = series.map((seriesItem) => {
                    return this.previewShareOfVoiceByVolumeOfMentions(seriesItem);
                });
                break;

            case ReportBlockTypeEnum.REFERRAL_TREND:
                previewSeries = series.map((seriesItem) => {
                    return this.previewReferralTrend(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.COVERAGE_SUMMARY:
                previewSeries = series.map((seriesItem) => {
                    return this.previewCoverageSummary(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                previewSeries = series.map(seriesItem => {
                    return this.previewProminenceTrend(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
                previewSeries = series.map(seriesItem => {
                    return this.previewProminenceCoverage(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                previewSeries = series.map((seriesItem) => {
                    return this.previewTop10FacetTypeByVolumeSeries(seriesItem, reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.socialMedia));
                });
                break;
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                previewSeries = series.map((seriesItem) => {
                    return this.previewReferralSources(seriesItem);
                });

                break;
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
                previewSeries = series.map((seriesItem) => {
                    return this.previewSocialCoverageByNetworkSeries(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.TOP_TERMS:
                previewSeries = series.map((seriesItem) => {
                    return this.previewTopTerms(seriesItem, displayElement);
                });
                break;
            default:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                previewSeries = series.map((seriesItem) => {
                    return this.previewCoverageImpactTrend(seriesItem);
                });
                break;
            case ReportBlockTypeEnum.TOP_POSTS:
                previewSeries = series.map((seriesItem) => {
                    return this.previewTopPosts(seriesItem, displayElement);
                });
                break;
        }

        return previewSeries;
    }

    previewCoverageImpactTrend(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.referralTypes = seriesItem.referralTypes;
        stubSeries.subSeries = seriesItem.subSeries;

        const stubData: DataPointModel[] = [];

        let dateRange = seriesItem.dateRange;
        if (!dateRange) {
            dateRange = new ReportDateRangeModel().setFromDateQuery(dateQueryInput24HoursOption.value.query);
            stubSeries.dateRange = dateRange;
        }

        let currentTime = this.getStartTime(dateRange);
        let endTime = this.getEndTime(dateRange);
        // add 0.5 day so that it rounds to the nearest date instead of begining date, this is how we get b/e data to show the date
        currentTime = moment(currentTime).add(12, 'hours').startOf('day').toDate().getTime();
        endTime = moment(endTime).add(12, 'hours').startOf('day').toDate().getTime();

        // Relative ranges should end before current time, or they'll show the current date as a bullet, trim 10ms off to account for it
        endTime -= 10;

        let lastYValues: number[] = null;
        const range = seriesItem.referralTypes?.length || 1;

        let lastTime = currentTime;
        for (; currentTime <= endTime; currentTime = moment(currentTime).add(1, 'days').toDate().getTime()) {
            let newYValues: number[] = [];

            if (lastYValues !== null) {
                for (let i = 0; i < range; i++) {
                    newYValues[i] = lastYValues[i] + (Math.round(Math.random() * 30) - 15) * 15;
                    if (newYValues[i] < 0) {
                        newYValues[i] = 0;
                    }
                }
            } else {
                newYValues = [];
                for (let i = 0; i < range; i++) {
                    newYValues[i] = Math.round(Math.random() * 100) * 15;
                }
            }

            const dataPoint: DataPointModel = {
                rawDateFacetFrom: lastTime,
                rawDateFacetTo: currentTime,
                xValue: lastTime,
            };

            lastYValues = newYValues.slice();

            if (seriesItem.type === SeriesTypeEnum.googleAnalytics) {
                // @NOTE @FUTURE, need to add other cases for other external data types
                if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS)) {
                    dataPoint[GoogleAnalyticsReportTypesEnum.UNIQUE_PAGEVIEWS] = newYValues.pop();
                }
                if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS)) {
                    dataPoint[GoogleAnalyticsReportTypesEnum.TOTAL_PAGEVIEWS] = newYValues.pop();
                }
                if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.SESSIONS)) {
                    dataPoint[GoogleAnalyticsReportTypesEnum.SESSIONS] = newYValues.pop();
                }
                if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.USERS)) {
                    dataPoint[GoogleAnalyticsReportTypesEnum.USERS] = newYValues.pop();
                }
            } else {
                dataPoint.yValue = newYValues.pop();
            }

            stubData.push(dataPoint);
            lastTime = currentTime;
        }

        stubSeries.dataProvider = new BehaviorSubject(stubData);

        return stubSeries;
    }

    previewReferralTrend(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.referralTypes = seriesItem.referralTypes;
        stubSeries.subSeries = seriesItem.subSeries;

        const stubData: DataPointModel[] = [];

        let dateRange = seriesItem.dateRange;
        if (!dateRange) {
            dateRange = new ReportDateRangeModel().setFromDateQuery(dateQueryInput30DaysOption.value.query);
            stubSeries.dateRange = dateRange;
        }

        let currentTime = this.getStartTime(dateRange);
        let endTime = this.getEndTime(dateRange);
        // add 0.5 day so that it rounds to the nearest date instead of begining date, this is how we get b/e data to show the date
        currentTime = moment(currentTime).add(12, 'hours').startOf('day').toDate().getTime();
        endTime = moment(endTime).add(12, 'hours').startOf('day').toDate().getTime();

        // Relative ranges should end before current time, or they'll show the current date as a bullet, trim 10ms off to account for it
        endTime -= 10;

        let lastYValues: number[] = null;
        const range = seriesItem.referralTypes?.length || 0;

        // if the resolution is greater than ~3 months, then step in increments of weeks, otherwise days are fine
        // day resolution starts to look messy around ~3months. could also randomly skip steps instead
        const smallestOneMonthInHours = 24 * 28;
        const stepSizeInDays = (dateRange.relativeEndOffsetH - dateRange.relativeStartOffsetH) <= (smallestOneMonthInHours * 3) ? 1 : 7;

        let lastTime = currentTime;
        for (; currentTime <= endTime; currentTime = moment(currentTime).add(stepSizeInDays, 'days').toDate().getTime()) {
            let newYValues: number[] = [];

            if (lastYValues !== null) {
                for (let i = 0; i < range; i++) {
                    newYValues[i] = lastYValues[i] + (Math.round(Math.random() * 30) - 15) * 15;
                    if (newYValues[i] < 0) {
                        newYValues[i] = 0;
                    }
                }
            } else {
                newYValues = [];
                for (let i = 0; i < range; i++) {
                    newYValues[i] = Math.round(Math.random() * 100) * 15;
                }
            }

            const dataPoint = {
                rawDateFacetFrom: lastTime,
                rawDateFacetTo: currentTime,
                xValue: lastTime,
            };

            lastYValues = newYValues.slice();
            if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS)) {
                dataPoint[GoogleAnalyticsReportTypesEnum.TOTAL_REFERRALS] = newYValues.reduce((v1, v2) => v1 + v2);
            }
            if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.WEB_REFERRALS)) {
                dataPoint[GoogleAnalyticsReportTypesEnum.WEB_REFERRALS] = newYValues.pop();
            }
            if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.SOCIAL_REFERRALS)) {
                dataPoint[GoogleAnalyticsReportTypesEnum.SOCIAL_REFERRALS] = newYValues.pop();
            }
            if (seriesItem.referralTypes.includes(GoogleAnalyticsReportTypesEnum.EMAIL_REFERRALS)) {
                dataPoint[GoogleAnalyticsReportTypesEnum.EMAIL_REFERRALS] = newYValues.pop();
            }

            stubData.push(dataPoint);
            lastTime = currentTime;
        }

        stubSeries.dataProvider = new BehaviorSubject(stubData);

        return stubSeries;
    }

    previewUnknownSeries(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        stubSeries.dataProvider = new BehaviorSubject([]);

        return stubSeries;
    }

    previewMediaTypeBreakdownSeries(seriesItem: ReportSeriesModel, type: ReportBlockTypeEnum): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        let mediaTypes = Array.from(mediaTypeToPieMap);
        if (!this.hasTiktok) {
            mediaTypes = mediaTypes.filter(([key]) => key !== MediaTypeEnum.TIKTOK);
        }

        if (!!seriesItem.styles.networkSelection) {
            mediaTypes = mediaTypes.filter(([key]) => seriesItem.styles.networkSelection.includes(key));
        }

        const stubData: DataPointModel[] = [];

        // This is so that they always appear in the order specified by the mediaTypeToPieMap
        for (const [key, mediaType] of mediaTypes) {
            if (!mediaType) {
                continue;
            }

            stubData.push({
                label: this.localizationService.transform(mediaType.label),
                id: key,
                xValue: Math.round(100 * Math.random()),
                color: this.userChartColors.mediaTypeColors[key],
                labelColor: mediaType.labelColor
            });
        }

        stubSeries.dataProvider = new BehaviorSubject(stubData);

        return stubSeries;
    }

    previewSentimentBreakdownSeries(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        const dataPoints: DataPointModel[] = [];

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.positive)) {
            const positiveCount = Math.round(Math.random() * 100);
            dataPoints.push({
                label: this.localizationService.transform('Sentiment.POSITIVE'),
                xValue: positiveCount,
                color: this.userChartColors.sentimentColors.positive,
                labelColor: chartColors.agprWhite,
                id: SearchCriteriaQuerySentimentsEnum.positive,
                value0: positiveCount
            });
        }

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.neutral)) {
            const neutralCount = Math.round(Math.random() * 100);
            dataPoints.push({
                label: this.localizationService.transform('Sentiment.NEUTRAL'),
                xValue: neutralCount,
                color: this.userChartColors.sentimentColors.neutral,
                labelColor: chartColors.agprBlack,
                id: SearchCriteriaQuerySentimentsEnum.neutral,
                value1: neutralCount
            });
        }

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.negative)) {
            const negativeCount = Math.round(Math.random() * 100);
            dataPoints.push({
                label: this.localizationService.transform('Sentiment.NEGATIVE'),
                xValue: negativeCount,
                color: this.userChartColors.sentimentColors.negative,
                labelColor: chartColors.agprWhite,
                id: SearchCriteriaQuerySentimentsEnum.negative,
                value2: negativeCount
            });
        }

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.notAvailable)) {
            const notAvailableCount = Math.round(Math.random() * 100);
            dataPoints.push({
                label: this.localizationService.transform('Sentiment.NOT_AVAILABLE'),
                xValue: notAvailableCount,
                color: this.userChartColors.sentimentColors.notAvailable,
                labelColor: chartColors.agprBlack,
                id: SearchCriteriaQuerySentimentsEnum.notAvailable,
                value3: notAvailableCount
            });
        }

        const sum = dataPoints.reduce((x, y) => x + y.xValue, 0) || 1;
        dataPoints.forEach((dataPoint, index) => {
            dataPoint.percent = dataPoint.xValue / sum * 100 || 0;
            dataPoint['value' + index + '_percent'] = dataPoint.xValue / sum * 100 || 0;
        });

        stubSeries.dataProvider = new BehaviorSubject(dataPoints);

        return stubSeries;
    }

    previewSentimentTrend(series: ReportSeriesModel[]): ReportSeriesModel[] {
        const seriesItem = series[0];
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.sentiments = seriesItem.sentiments.slice();
        const data: DataPointModel[] = [];

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.positive)) {
            data.push(...this.getDataOverTime(seriesItem));
            data.forEach(dataPoint => {
                dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.positive}`] = dataPoint.yValue || 0;
                delete dataPoint.yValue;
            });
        }

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.neutral)) {
            const neutralData = this.getDataOverTime(seriesItem);
            if (data.length === 0) {
                data.push(...neutralData);
                data.forEach(dataPoint => {
                    dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.neutral}`] = dataPoint.yValue || 0;
                    delete dataPoint.yValue;
                });
            } else {
                data.forEach((dataPoint, index) => {
                    dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.neutral}`] = neutralData[index]?.yValue || 0;
                });
            }
        }

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.negative)) {
            const negativeData = this.getDataOverTime(seriesItem);
            if (data.length === 0) {
                data.push(...negativeData);
                data.forEach(dataPoint => {
                    dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.negative}`] = dataPoint.yValue || 0;
                    delete dataPoint.yValue;
                });
            } else {
                data.forEach((dataPoint, index) => {
                    dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.negative}`] = negativeData[index]?.yValue || 0;
                });
            }
        }

        if (seriesItem.sentiments.includes(SearchCriteriaQuerySentimentsEnum.notAvailable)) {
            const notAvailableData = this.getDataOverTime(seriesItem);
            if (data.length === 0) {
                data.push(...notAvailableData);
                data.forEach(dataPoint => {
                    dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.notAvailable}`] = dataPoint.yValue || 0;
                    delete dataPoint.yValue;
                });
            } else {
                data.forEach((dataPoint, index) => {
                    dataPoint[`yValue_${SearchCriteriaQuerySentimentsEnum.notAvailable}`] = notAvailableData[index]?.yValue || 0;
                });
            }
        }

        stubSeries.dataProvider = new BehaviorSubject<DataPointModel[]>(data);

        return [stubSeries];
    }

    previewTop10FacetTypeByVolumeSeries(seriesItem: ReportSeriesModel, names: string[]): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        const totalResults = 1000 + Math.round(Math.random() * 1000); // Maximum of 2000

        const stubData: DataPointModel[] = names.map(name => {
            const count = Math.round(Math.random() * 100);
            return {
                label: name,
                xValue: count, // todo: remove this
                value0: count
            };
        });

        stubData.forEach(
            (data) => {
                data.percent = ((data.xValue / totalResults) * 100).toFixed(2);
                data['value0_percent'] = ((data.xValue / totalResults) * 100).toFixed(2);
            }
        );

        stubData.sort((a, b) => b.xValue - a.xValue);
        stubSeries.dataProvider = new BehaviorSubject(stubData);

        return stubSeries;
    }

    previewSocialCoverageByNetworkSeries(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.hasPreviousPeriod = seriesItem.hasPreviousPeriod;

        const names: string[] = [];

        if (stubSeries?.styles?.networkSelection) {
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.TWITTER)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.TWITTER}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.FACEBOOK)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.FACEBOOK}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.INSTAGRAM)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.INSTAGRAM}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.YOUTUBE)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.YOUTUBE}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.REDDIT)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.REDDIT}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.TIKTOK)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.TIKTOK}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.LINKEDIN)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.LINKEDIN}`));
            }
            if (includes(stubSeries?.styles?.networkSelection, MediaTypeEnum.BLUESKY)) {
                names.push(this.localizationService.transform(`MediaType.${MediaTypeEnum.BLUESKY}`));
            }
        }

        const totalResults = 1000 + Math.round(Math.random() * 1000); // Maximum of 2000

        const stubData: DataPointModel[] = names.map(
            name => {
                return {
                    id: name === 'X' ? MediaTypeEnum.TWITTER : name.toUpperCase(),
                    label: name,
                    xValue: Math.round(Math.random() * 100)
                };
            }
        );

        stubData.forEach(
            (data) => {
                data.percent = ((data.xValue / totalResults) * 100).toFixed(2);
            }
        );

        stubData.sort((a, b) => b.xValue - a.xValue);
        stubSeries.dataProvider = new BehaviorSubject(stubData);

        if (stubSeries.hasPreviousPeriod) {
            const previousData: DataPointModel[] = names.map(
                name => {
                    return {
                        id: name === 'X' ? MediaTypeEnum.TWITTER : name.toUpperCase(),
                        label: name,
                        xValue: Math.round(Math.random() * 100)
                    };
                }
            );
            stubSeries.previousPeriodDataProvider = new BehaviorSubject(previousData);
        }

        return stubSeries;
    }

    previewGeographyBreakdownByVolumeSeries(seriesItem: ReportSeriesModel, displayElement: ChartDisplayElementModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        if (!stubSeries || !stubSeries.styles) {
            console.log('returns prematurally');
            return stubSeries;
        }
        stubSeries.dataProvider = new BehaviorSubject(null);

        switch (stubSeries.styles.geographyScale) {
            case GeographyTypesEnum.country:
                this.setStubDataForCountry(stubSeries, displayElement.mapChartColorScheme);
                break;
            default:
                this.setStubDataForWorld(stubSeries, displayElement.mapChartColorScheme);
        }

        return stubSeries;
    }

    /**
     * Sets mock data for a world chart
     */
    setStubDataForWorld(series: ReportSeriesModel, colorScheme: MapChartColorSchemeEnum): void {
        const stubData: DataPointModel[] = [
            {
                label: 'Canada',
                id: 'CA',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'United States',
                id: 'US',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'United Kingdom',
                id: 'GB',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'Germany',
                id: 'DE',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'Switzerland',
                id: 'CH',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'Norway',
                id: 'NO',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'Nigeria',
                id: 'NG',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'Turkey',
                id: 'TR',
                xValue: Math.round(Math.random() * 100)
            },
            {
                label: 'India',
                id: 'IN',
                xValue: Math.round(Math.random() * 100)
            },
        ];

        if (colorScheme === MapChartColorSchemeEnum.CHOROPLETH) {
            stubData.push(
                {
                    label: 'Spain',
                    id: 'ES',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'Italy',
                    id: 'IT',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'France',
                    id: 'FR',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'Mexico',
                    id: 'MX',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'France',
                    id: 'FR',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'Russia',
                    id: 'RU',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'China',
                    id: 'CN',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'Australia',
                    id: 'AU',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'South Afria',
                    id: 'ZA',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'Brazil',
                    id: 'BR',
                    xValue: Math.round(Math.random() * 100)
                },
                {
                    label: 'Argentina',
                    id: 'AR',
                    xValue: Math.round(Math.random() * 100)
                }
            );
        }

        series.dataProvider.next(this.filterDataPointModels(stubData, colorScheme));
    }

    /**
     * Sets mock data for a given country
     */
    setStubDataForCountry(series: ReportSeriesModel, colorScheme: MapChartColorSchemeEnum): void {
        if (!series || !series.styles || !series.styles.regionIsoCode) {
            series.dataProvider.next([]);

            return;
        }
        const countryJournodexId = +this.journodexSearchService.getCountryByIsoCode(series.styles.regionIsoCode + '').id;

        const geographyModel = new JournodexGeographyModel();
        geographyModel.id = countryJournodexId;
        geographyModel.geographicType = GeographyTypesEnum.country;
        geographyModel.country = <JournodexGeographicEntityModel>{ id: countryJournodexId, name: null, names: null };

        this.journodexSearchService.getChildRegions(geographyModel).subscribe(
            provinces => {
                const stubData: DataPointModel[] = provinces.map(province => {
                    return {
                        label: province.province.name,
                        id: `${series.styles.regionIsoCode}-${province.isoCode}`,
                        xValue: Math.round(Math.random() * 100)
                    };
                });
                series.dataProvider.next(this.filterDataPointModels(stubData, colorScheme));
            }
        );
    }

    /**
     * Filters the data so that only the top 10 items and an aggregate of the others appear in the results
     */
    filterDataPointModels(data: DataPointModel[], colorScheme: MapChartColorSchemeEnum): DataPointModel[] {
        // leave other out, so that it's always added last
        const other: DataPointModel = {
            label: 'Other',
            xValue: Math.round(Math.random() * 100) + 1,
            color: chartColors.agprOther
        };

        const addOther: boolean = data.length >= 10 && colorScheme !== MapChartColorSchemeEnum.CHOROPLETH;

        let total = 0;
        if (addOther) {
            total = other.xValue;
        }
        data.forEach((item) => {
            item.xValue += 1;
            total += item.xValue;
        });
        data.forEach((item) => {
            item.yValue = item.xValue / total * 100;
        });
        data.sort((a, b) => b.xValue - a.xValue);

        other.yValue = other.xValue / total * 100;

        if (addOther) {
            data = data.slice(0, 10);
            data.push(other);
        }

        return data;
    }

    previewShareOfVoiceByVolumeOfMentions(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        const stubData: DataPointModel[] = [];

        stubData.push({
            label: stubSeries.label,
            xValue: Math.round(Math.random() * 100),
            color: seriesItem.color
        });

        if (seriesItem.hasPreviousPeriod) {
            stubData[0].xValuePreviousPeriod = seriesItem.dateRange.type === DateQueryTypeEnum.ALL ? 0 : Math.round(Math.random() * 100);
        }

        stubSeries.dataProvider = new BehaviorSubject(stubData);
        return stubSeries;
    }

    /**
     * Sets the dateFacet slice size based on the dateQuery range, if it's greater than 1 day, use days, else, use hours
    */
    private determineSliceSize(dateQuery: ReportDateRangeModel): number {
        if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            let hoursDifference: number;
            if (dateQuery.absoluteRange) {
                const start = moment(dateQuery.absoluteRange.start);
                const end = moment(dateQuery.absoluteRange.end);

                hoursDifference = moment.duration(end.diff(start)).asHours();
            } else {
                // absoluteDate case
                hoursDifference = 24;
            }

            if (hoursDifference <= 24) {
                return 3600000;
            } else {
                return 86400000;
            }
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE) {
            if (Math.abs(dateQuery.relativeEndOffsetH - dateQuery.relativeStartOffsetH) <= 24) {
                // The date range covers 24 hours or less
                return 3600000;
            } else {
                // The date range covers more than 24 hours
                return 86400000;
            }
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const hoursDifference = DateQueryModel.getRelativeTimezoneDiffAsHours(dateQuery.relativeTimezoneDateType, dateQuery.timezone);
            if (hoursDifference <= 24 || dateQuery.relativeTimezoneDateType === RelativeTimezoneDateTypeEnum.LAST_24_HOURS) {
                // The date range covers 24 hours or less
                return 3600000;
            } else {
                // The date range covers more than 24 hours
                return 86400000;
            }
        } else {
            // 'ALL' should be daily slices
            return 86400000;
        }
    }
    private getStartTime(dateQuery: ReportDateRangeModel): number {
        if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            let start;
            if (dateQuery.absoluteRange) {
                start = moment(dateQuery.absoluteRange.start);
            } else {
                start = moment(dateQuery.absoluteDate);
            }
            return start.toDate().getTime();
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const start = DateQueryModel.getStartDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone);

            return start.toDate().getTime();
        } else {
            const start = moment(moment.now());
            start.add(dateQuery.relativeStartOffsetH, 'hours');
            return start.toDate().getTime();
        }
    }
    private getEndTime(dateQuery: ReportDateRangeModel): number {
        if (dateQuery.type === DateQueryTypeEnum.ABSOLUTE) {
            let end;
            if (dateQuery.absoluteRange) {
                end = moment(dateQuery.absoluteRange.end);
            } else {
                end = moment(dateQuery.absoluteDate);
            }
            return end.toDate().getTime();
        } else if (dateQuery.type === DateQueryTypeEnum.RELATIVE_TIMEZONE) {
            const end = DateQueryModel.getEndDateForRelativeTimezone(dateQuery.relativeTimezoneDateType, dateQuery.timezone);

            return end.toDate().getTime();
        } else {
            const end = moment(moment.now());
            end.add(dateQuery.relativeEndOffsetH, 'hours');
            return end.toDate().getTime();
        }
    }

    getDataOverTime(seriesItem: ReportSeriesModel, factor: number = 1, maxValue?: number, timeInterval?: ChartTimeIntervalsEnum): DataPointModel[] {
        let dateRange = seriesItem.dateRange;

        if (!dateRange) {
            dateRange = new ReportDateRangeModel();
            dateRange.timezone = this.preferences.timeZone;
        }

        const stubData = [];
        const sliceSize = this.determineSliceSize(dateRange);
        let currentTime;
        let endTime;
        if (dateRange.type !== DateQueryTypeEnum.ALL) {
            currentTime = this.getStartTime(dateRange);
            endTime = this.getEndTime(dateRange);
        } else {
            let durationBase = 15;
            if (timeInterval === ChartTimeIntervalsEnum.YEAR) {
                durationBase = 365;
            } else if (timeInterval === ChartTimeIntervalsEnum.QUARTER) {
                durationBase = 90;
            }
            // The values here allow for a maximum start time of 60 days ago, lasting at least 15 days and ending at most with the current date
            currentTime = moment(new Date()).subtract((Math.random() * 45) + durationBase, 'days').toDate().getTime();
            endTime = moment(currentTime).add((Math.random() * 44) + durationBase, 'days').toDate().getTime();
        }


        // Relative ranges should end before current time, or they'll show the current date as a bullet, trim 10ms off to account for it
        if (dateRange.type !== DateQueryTypeEnum.ABSOLUTE) {
            endTime -= 10;
        }

        const sliceUnit: string = sliceSize === 3600000 ? 'hours' : 'days';
        let lastYValue: number = null;
        for (; currentTime <= endTime; currentTime = moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()) {
            let newYValue: number;
            if (lastYValue !== null) {
                // Base the new value off the old one, new value = oldValue +- (15 * factor)
                // To minimize large deltas and create a smoother line
                newYValue = lastYValue + ((Math.round(Math.random() * 30) - 15) * factor);
                if (newYValue < 0) {
                    newYValue = 0;
                }
            } else {
                newYValue = Math.round(Math.random() * 100) * factor;
            }

            if (maxValue) {
                newYValue = Math.min(newYValue, maxValue);
            }
            const data = {
                yValue: newYValue,
                xValue: moment(moment(currentTime).tz(this.preferences.timeZone).format('YYYY-MM-DD HH:mm:ss')).valueOf(),
                label: new Date(currentTime).toDateString(),
                mentionCount: Math.round(Math.random() * 100),
                rawDateFacetFrom: currentTime,
                rawDateFacetTo: moment(currentTime).add(1, sliceUnit as DurationInputArg2).toDate().getTime()
            };
            if (seriesItem.metrics?.length > 0) {
                seriesItem.metrics.forEach((metric, index) => {
                    data[`yValue_${metric}`] =  Math.round(Math.random() * 100) * factor;
                    data[`yValue${index}`] =  Math.round(Math.random() * 100) * factor;
                });
            }
            stubData.push(data);
            lastYValue = newYValue;
        }

        return stubData;
    }

    previewCoverageOverTimeSeries(
        seriesItem: ReportSeriesModel, factor: number = 1, maxValue?: number, timeInterval?: ChartTimeIntervalsEnum
    ): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        let dateRange = seriesItem.dateRange;
        if (!dateRange) {
            dateRange = new ReportDateRangeModel();
            dateRange.timezone = this.preferences.timeZone;
            stubSeries.dateRange = dateRange;
        }

        stubSeries.dataProvider = new BehaviorSubject(this.getDataOverTime(seriesItem, factor, maxValue, timeInterval));
        stubSeries.metric = seriesItem.metric;
        stubSeries.metrics = seriesItem.metrics || [];
        return stubSeries;
    }

    previewProminenceTrend(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const series = this.previewCoverageOverTimeSeries(seriesItem, 0.04, 5);
        series.chartType = ReportBlockTypeEnum.PROMINENCE_OVER_TIME;
        return series;
    }

    previewProminenceCoverage(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        stubSeries.chartType = ReportBlockTypeEnum.PROMINENCE_BY_REACH;
        let data: number[] = [];
        for (let i = 1; i <= 4; i++) {
            data.push(Math.round(Math.random() * 100 * i));
        }
        data = data.sort((a, b) => b - a);
        const total = data.reduce((acc, cur) => acc + cur, 0);

        const stubData = [
            {
                xValue: 1,
                yValue: 1,
                label: this.localizationService.transform('Reports.prominenceByReach.leader'),
                percent: Math.round((data[0] * 100) / total),
                count: data[0],
                color: seriesItem.color
            },
            {
                xValue: 1,
                yValue: 0,
                label: this.localizationService.transform('Reports.prominenceByReach.prevalent'),
                percent: Math.round((data[1] * 100) / total),
                count: data[1],
                color: seriesItem.color
            },
            {
                xValue: 0,
                yValue: 1,
                label: this.localizationService.transform('Reports.prominenceByReach.notable'),
                percent: Math.round((data[2] * 100) / total),
                count: data[3],
                color: seriesItem.color
            },
            {
                xValue: 0,
                yValue: 0,
                label: this.localizationService.transform('Reports.prominenceByReach.niche'),
                percent: Math.round((data[3] * 100) / total),
                count: data[3],
                color: seriesItem.color
            }
        ];

        stubSeries.dataProvider = new BehaviorSubject(stubData);

        return stubSeries;
    }


    /**
     * Converts the date range to a string in the format {start date} - {end date}
     *
     * @TODO: This function probably doesn't belong here, can't put it in reportsService because it causes a circular dependency
     * Don't want to make a helper service for a single function, can't put it in the model because it needs the localization service
     */
    public dateRangeToString(series: ReportSeriesModel[], chartType?: ReportBlockTypeEnum, format?: string): string {
        if (!series || !series.length || !this.preferences) {
            return '';
        }

        const dateRange: ReportDateRangeModel = series[0].dateRange;
        const dateFormat = format ? format : this.preferences.getDateFormatString(true);

        let start: string;
        let end: string;
        switch (dateRange.type) {
            case DateQueryTypeEnum.ABSOLUTE:
                if (dateRange.absoluteRange) {
                    start = moment(dateRange.absoluteRange.start).tz(this.preferences.timeZone).format(dateFormat);
                    end = moment(dateRange.absoluteRange.end).tz(this.preferences.timeZone).format(dateFormat);

                    if (start && end) {
                        return start + ' - ' + end;
                    }
                } else {
                    return moment(dateRange.absoluteDate).tz(this.preferences.timeZone).format(dateFormat);
                }
                break;

            case DateQueryTypeEnum.RELATIVE:
                start = moment().add(dateRange.relativeStartOffsetH, 'hours').tz(this.preferences.timeZone).format(dateFormat);
                end = moment().add(dateRange.relativeEndOffsetH, 'hours').tz(this.preferences.timeZone).format(dateFormat);

                if (
                    chartType === ReportBlockTypeEnum.REFERRAL_TREND
                    || chartType === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
                ) {
                    // Google Analytics charts have highly variable relative date ranges, up to a year back
                    // we could want to provide a 'preferredResolution' parameter or something that would let us choose to prefix
                    // with 'last X days/months', but for now just show the actual dates.
                    return `${start} - ${end}`;
                }

                const diff = dateRange.relativeEndOffsetH - dateRange.relativeStartOffsetH;
                let relativeDescription = '';

                if (diff === 24) {
                    relativeDescription = this.localizationService.transform('last24Hours');

                } else {
                    relativeDescription = this.localizationService.transform('lastNDays', {'days': Math.floor(diff / 24)});
                }

                if (start && end) {
                    return `${relativeDescription} (${start} - ${end})`;
                }
                break;

            case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                start = DateQueryModel.getStartDateForRelativeTimezone(dateRange.relativeTimezoneDateType, this.preferences.timeZone).format(dateFormat);
                end = DateQueryModel.getEndDateForRelativeTimezone(dateRange.relativeTimezoneDateType, this.preferences.timeZone).format(dateFormat);

                if (
                    chartType === ReportBlockTypeEnum.REFERRAL_TREND
                    || chartType === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
                ) {
                    // Google Analytics charts have highly variable relative date ranges, up to a year back
                    // we could want to provide a 'preferredResolution' parameter or something that would let us choose to prefix
                    // with 'last X days/months', but for now just show the actual dates.
                    return `${start} - ${end}`;
                }

                const relativeTimezoneDescription = this.localizationService.transform(`Common.dateQueryInput.${dateRange.relativeTimezoneDateType}`);

                if (start && end) {
                    return `${relativeTimezoneDescription} (${start} - ${end})`;
                }
                break;
            case DateQueryTypeEnum.ALL:
                return this.localizationService.transform('all');
        }

        return '';
    }

    previewCoverageSummary(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        const stubData: DataPointModel[] = [];

        stubData.push(
            {
                id: ChartMetricsEnum.volumeOfMentions as string,
                label: this.localizationService.transform('Reports.totalMentions'),
                csvLabel: this.localizationService.transform('Reports.totalMentions.csv'),
                xValue: Math.round(Math.random() * 100),
            },
            {
                id: ChartMetricsEnum.audienceReach as string,
                label: this.localizationService.transform('Reports.audienceReach'),
                csvLabel: this.localizationService.transform('Reports.audienceReach.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.IMPRESSIONS as string,
                label: this.localizationService.transform('Reports.audienceReach'),
                csvLabel: this.localizationService.transform('Reports.audienceReach.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.audienceReach as string,
                label: this.localizationService.transform('Reports.audienceReach'),
                csvLabel: this.localizationService.transform('Reports.audienceReach.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.ave as string,
                label: this.localizationService.transform('Reports.ave'),
                csvLabel: this.localizationService.transform('Reports.ave.csv'),
                xValue: Math.round(Math.random() * 10000) / 100,
                currency: 'USD',
                useSignificantDigits: 1,

            },
            {
                id: ChartMetricsEnum.positiveSentimentPercentage as string,
                label: this.localizationService.transform('Reports.positiveSentimentPercentage'),
                csvLabel: this.localizationService.transform('Reports.positiveSentimentPercentage.csv'),
                xValue: Math.round(Math.random() * 10000) / 100,
                percent: Math.round(Math.random() * 10000) / 100,
                useSignificantDigits: 2,
            },
            {
                id: ChartMetricsEnum.neutralSentimentPercentage as string,
                label: this.localizationService.transform('Reports.neutralSentimentPercentage'),
                csvLabel: this.localizationService.transform('Reports.neutralSentimentPercentage.csv'),
                xValue: Math.round(Math.random() * 10000) / 100,
                percent: Math.round(Math.random() * 10000) / 100,
                useSignificantDigits: 2,
            },
            {
                id: ChartMetricsEnum.negativeSentimentPercentage as string,
                label: this.localizationService.transform('Reports.negativeSentimentPercentage'),
                csvLabel: this.localizationService.transform('Reports.negativeSentimentPercentage.csv'),
                xValue: Math.round(Math.random() * 10000) / 100,
                percent: Math.round(Math.random() * 10000) / 100,
                useSignificantDigits: 2,
            },
            {
                id: ChartMetricsEnum.ENGAGEMENTS as string,
                label: this.localizationService.transform('Reports.engagements'),
                csvLabel: this.localizationService.transform('Reports.engagements.csv'),
                xValue: Math.round(Math.random() * 10000),
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.ENGAGEMENTS_PER_POST as string,
                label: this.localizationService.transform('Reports.engagementsPerPost'),
                csvLabel: this.localizationService.transform('Reports.engagementsPerPost.csv'),
                xValue: Math.round(Math.random() * 1000),
                useSignificantDigits: 2,
            },
            {
                id: ChartMetricsEnum.ENGAGEMENT_RATE as string,
                label: this.localizationService.transform('Reports.engagementRate'),
                csvLabel: this.localizationService.transform('Reports.engagementRate.csv'),
                xValue: Math.round(Math.random() * 10000) / 100,
                percent: Math.round(Math.random() * 10000) / 100,
                useSignificantDigits: 2,
            },
            {
                id: ChartMetricsEnum.APPLAUSE as string,
                label: this.localizationService.transform('Reports.engagements.applause'),
                csvLabel: this.localizationService.transform('Reports.engagements.applause.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.CONVERSATION as string,
                label: this.localizationService.transform('Reports.engagements.conversation'),
                csvLabel: this.localizationService.transform('Reports.engagements.conversation.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.AMPLIFICATION as string,
                label: this.localizationService.transform('Reports.engagements.amplification'),
                csvLabel: this.localizationService.transform('Reports.engagements.amplification.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            },
            {
                id: ChartMetricsEnum.VIEWS as string,
                label: this.localizationService.transform('Reports.engagements.views'),
                csvLabel: this.localizationService.transform('Reports.engagements.views.csv'),
                xValue: Math.round(Math.random() * 10000),
                numberFormat: '1.0',
                useSignificantDigits: 1,
            }
        );

        stubSeries.dataProvider = new BehaviorSubject(stubData);

        if (seriesItem.hasPreviousPeriod) {
            const previousPeriod: DataPointModel[] = cloneDeep(stubData);
            previousPeriod.forEach(item => {
                if (seriesItem.dateRange.type !== DateQueryTypeEnum.ALL) {
                    item.xValue = Math.round(Math.random() * 10000) / 100;
                }
            });
            stubSeries.previousPeriodDataProvider = new BehaviorSubject(previousPeriod);
            stubSeries.hasPreviousPeriod = true;
        }

        return stubSeries;
    }

    previewReferralSources(seriesItem: ReportSeriesModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.metric = seriesItem.metric;
        const dataPoints: DataPointModel[] = [];
        const seriesIds: ExternalChartMetricsEnum[] = [];

        if (stubSeries.metric === ExternalChartMetricsEnum.usersNewReturning) {
            seriesIds.push(ExternalChartMetricsEnum.usersNew, ExternalChartMetricsEnum.usersReturning);
        } else if (stubSeries.metric === ExternalChartMetricsEnum.sessionsNewReturning) {
            seriesIds.push(ExternalChartMetricsEnum.sessionsNew, ExternalChartMetricsEnum.sessionsReturning);
        } else {
            seriesIds.push(stubSeries.metric as ExternalChartMetricsEnum);
        }

        reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.referralSources).forEach(source => {
            seriesIds.forEach(id => {
                dataPoints.push(
                    {
                        source,
                        yValue: Math.round(Math.random() * 100),
                        id
                    }
                );
            });
        });

        stubSeries.dataProvider = new BehaviorSubject(dataPoints);

        return stubSeries;
    }

    getListChartPreviewData(block: BlockModel): DataPointModel[] {
        const blockType = block.type.key;
        const displayElement = block.displayElement as ChartDisplayElementModel;
        const now = new Date().getTime();
        const data = [];

        switch (blockType) {
            case ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY:
                for (let i = 0; i < displayElement.numOfItems; i++) {
                    const randomInt = Math.floor(Math.random() * 10);
                    const eventValues = Object.values(TargetingEventsEnum);
                    const eventEnum = eventValues[randomInt % eventValues.length];

                    data.push({
                        icon: userEventToIconMap.get(eventEnum),
                        date: now - i * 10000000,
                        user: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                        event: `Dashboard.chart.targetingEvent.${eventEnum}`,
                        title: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.titles)[randomInt],
                    });
                }
                break;
            case ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY:
                for (let i = 0; i < displayElement.numOfItems; i++) {
                    const randomInt = Math.floor(Math.random() * 10);
                    const eventValues = Object.values(DistributionStatus);
                    const eventEnum = eventValues[randomInt % eventValues.length];
                    const statusString = this.localizationService.transform(eventEnum);

                    let tooltipInfo =
                        this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.created', {
                            createdBy: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                            createdDate: moment(now - i * 10000000).format('MMM DD, YYYY')
                        })
                        + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.modified', {
                            modifiedBy: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                            modifiedDate: moment(now - i * 10000000 + randomInt * 1000000).format('MMM DD, YYYY')
                        });

                    if (eventEnum === DistributionStatus.sent || eventEnum === DistributionStatus.scheduled) {
                        tooltipInfo = tooltipInfo + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentBy', {
                            sentBy: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                        });
                    }

                    data.push({
                        smallIcon: userEventToIconMap.get(eventEnum),
                        date: now - i * 10000000 + randomInt * 1000000,
                        title: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.titles)[randomInt],
                        status: statusString.charAt(0) + statusString.toLocaleLowerCase().slice(1),
                        tooltipInfo
                    });
                }
                break;
            case ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS:
                for (let i = 0; i < displayElement.numOfItems; i++) {
                    const randomInt = Math.floor(Math.random() * 10);
                    const value0 = Math.floor(Math.random() * 100);
                    const value1 = Math.floor(Math.random() * 100);
                    const value2 = Math.floor(Math.random() * 100);
                    const value3 = Math.floor(Math.random() * 100);

                    // prepare tooltip info
                    const tooltipInfo =
                            this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.created', {
                                createdBy: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                                createdDate: moment(now - i * 10000000 ).format('MMM DD, YYYY')
                            })
                            + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.modified', {
                                modifiedBy: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                                modifiedDate: moment(now - i * 10000000 + randomInt * 1000000).format('MMM DD, YYYY')
                            })
                            + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentBy', {
                                sentBy: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.authorNames)[randomInt],
                            })
                            + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentDate', {
                                sentDate: moment(now - i * 10000000 + randomInt * randomInt * 1000000).format('MMM DD, YYYY'),
                            });

                    data.push({
                        title: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.titles)[randomInt],
                        total: value0 + value1 + value2 + value3,
                        label0: 'Dashboard.chart.recentDistributionReports.delivered',
                        label1: 'Dashboard.chart.recentDistributionReports.opens',
                        label2: 'Dashboard.chart.recentDistributionReports.clicks',
                        label3: 'Dashboard.chart.recentDistributionReports.undelivered',
                        value0: value0,
                        value1: value1,
                        value2: value2,
                        value3: value3,
                        tooltipInfo
                    });
                }
                break;
            case ReportBlockTypeEnum.RECENT_SURGE_ALERTS:
                for (let i = 0; i < displayElement.numOfItems; i++) {
                    const randomInt = Math.floor(Math.random() * 10);
                    const eventValues = Object.values(AlertSurgeMetricEventsEnum);
                    const eventEnum = eventValues[randomInt % eventValues.length];

                    const dataPoint = {
                        smallIcon: 'fa-kit fa-agpr-momentum',
                        date: now - i * 10000000,
                        title: reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.titles)[randomInt],
                        surgePercentageChangePositive: Math.ceil(Math.random() * 400),
                        surgePercentageChangeNegative: Math.ceil(Math.random() * 400),
                        label: `Alerts.surge.metrics.${eventEnum}`,
                        metric: eventEnum
                    };

                    data.push(dataPoint);
                }
                break;
        }

        return data;
    }

    previewCoverageComparison(seriesItem: ReportSeriesModel, index: number): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.hasPreviousPeriod = seriesItem.hasPreviousPeriod;
        stubSeries.metric = seriesItem.metric;
        const stubData: DataPointModel[] = [{
            id: index + '',
            label: stubSeries.label,
            xValue: Math.round(Math.random() * 100)
        }];

        stubSeries.dataProvider = new BehaviorSubject(stubData);

        if (stubSeries.hasPreviousPeriod) {
            const previousData: DataPointModel[] = [{
                id: index + '',
                label: stubSeries.label,
                xValue: Math.round(Math.random() * 100)
            }];

            stubSeries.previousPeriodDataProvider = new BehaviorSubject(previousData);
        }

        return stubSeries;
    }

    previewTopTerms(seriesItem: ReportSeriesModel, displayElement: ChartDisplayElementModel): WordCloudSeriesModel {
        const stubSeries = new WordCloudSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);
        stubSeries.metric = seriesItem.metric;
        stubSeries.sentimentType = null;
        let tags: string[] = [];
        switch (displayElement.termType) {
            case TermTypesEnum.WORDS:
                tags = [...reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.words)];
                break;
            case TermTypesEnum.HANDLES:
                tags = [...reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.handles)];
                break;
            case TermTypesEnum.HASHTAGS:
                tags = [...reportsMockDataLabelMap.get(ReportsMockDataLabelTypesEnum.hashtags)];
                break;
        }
        const stubData: DataPointModel[] = [];
        tags = tags.slice(0, displayElement.numOfItems);

        if (displayElement.chartSpecificType === ChartSpecificTypesEnum.WORD_CLOUD) {
            tags.forEach(tag => {
                stubData.push({
                    tag,
                    count: Math.round(Math.random() * 100000)
                });
            });
        } else {
            tags.forEach(tag => {
                stubData.push({
                    label: tag,
                    count: Math.round(Math.random() * 100000)
                });
            });
            stubData.sort((a, b) => +b.count - +a.count );
            let i = 0;
            stubData.forEach(item => {
                item.id = i + '';
                i++;
            });
        }

        stubSeries.dataProvider = new BehaviorSubject(stubData);
        return stubSeries;
    }

    previewTopPosts(seriesItem: ReportSeriesModel, displayElement: ChartDisplayElementModel): ReportSeriesModel {
        const stubSeries = new ReportSeriesModel(seriesItem.toApiModel(), seriesItem.chartType);

        if (displayElement.isCustomSortFieldValid(seriesItem.chartType)) {
            stubSeries.mentionProvider = new BehaviorSubject(displayElement.topCuratedMentions);
            return stubSeries;
        }

        const studData: any[] = [
            {
                view: Math.round(Math.random() * 1000),
                like: Math.round(Math.random() * 1000),
                share: Math.round(Math.random() * 1000),
                reply: Math.round(Math.random() * 1000),
                quote: null,
                engagements: Math.round(Math.random() * 1000),
                engagementRate: Math.random(),
                id: '1',
                author: {
                    name: 'Handle 1'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.TIKTOK}`,
                    id: MediaTypeEnum.TIKTOK,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.TIKTOK),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.TIKTOK),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.TIKTOK),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.TIKTOK}`
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: '@Handle1',
                socialAvatarUrl: null,
            },
            {
                view: Math.round(Math.random() * 1000),
                like: Math.round(Math.random() * 1000),
                share: Math.round(Math.random() * 1000),
                reply: Math.round(Math.random() * 1000),
                quote: null,
                engagements: Math.round(Math.random() * 1000),
                engagementRate: Math.random(),
                id: '2',
                author: {
                    name: 'Handle 2'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                headline: 'Lorem Ipsum is simply dummy text',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.YOUTUBE}`,
                    id: MediaTypeEnum.YOUTUBE,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.YOUTUBE),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.YOUTUBE),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.YOUTUBE),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.YOUTUBE}`
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: '@Handle2',
                socialAvatarUrl: null,
            },
            {
                view: null,
                like: null,
                share: null,
                reply: null,
                quote: null,
                engagements: 0,
                engagementRate: 0,
                id: '3',
                author: {
                    name: 'Handle 3'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                headline: 'Lorem Ipsum is simply dummy text',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.REDDIT}`,
                    id: MediaTypeEnum.REDDIT,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.REDDIT),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.REDDIT),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.REDDIT),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.REDDIT}`,
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: 'u/@Handle3',
                socialAvatarUrl: null,
            },
            {
                view: Math.round(Math.random() * 1000),
                like: Math.round(Math.random() * 1000),
                share: Math.round(Math.random() * 1000),
                reply: Math.round(Math.random() * 1000),
                quote: null,
                engagements: Math.round(Math.random() * 1000),
                engagementRate: Math.random(),
                id: '4',
                author: {
                    name: 'Handle 4'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.FACEBOOK}`,
                    id: MediaTypeEnum.FACEBOOK,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.FACEBOOK),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.FACEBOOK),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.FACEBOOK),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.FACEBOOK}`,
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: '@Handle4',
                socialAvatarUrl: null,
            },
            {
                view: Math.round(Math.random() * 1000),
                like: Math.round(Math.random() * 1000),
                share: Math.round(Math.random() * 1000),
                reply: Math.round(Math.random() * 1000),
                quote: null,
                engagements: Math.round(Math.random() * 1000),
                engagementRate: Math.random(),
                id: '5',
                author: {
                    name: 'Handle 5'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.TWITTER}`,
                    id: MediaTypeEnum.TWITTER,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.TWITTER),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.TWITTER),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.TWITTER),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.TWITTER}`,
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: '@Handle5',
                socialAvatarUrl: null,
            },
            {
                view: Math.round(Math.random() * 1000),
                like: Math.round(Math.random() * 1000),
                share: Math.round(Math.random() * 1000),
                reply: Math.round(Math.random() * 1000),
                quote: null,
                engagements: Math.round(Math.random() * 1000),
                engagementRate: Math.random(),
                id: '6',
                author: {
                    name: 'Handle 6'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.LINKEDIN}`,
                    id: MediaTypeEnum.LINKEDIN,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.LINKEDIN),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.LINKEDIN),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.LINKEDIN),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.LINKEDIN}`,
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: '@Handle6',
                socialAvatarUrl: null,
            },
            {
                view: Math.round(Math.random() * 1000),
                like: Math.round(Math.random() * 1000),
                share: Math.round(Math.random() * 1000),
                reply: Math.round(Math.random() * 1000),
                quote: null,
                engagements: Math.round(Math.random() * 1000),
                engagementRate: Math.random(),
                id: '7',
                author: {
                    name: 'Handle 7'
                },
                body: 'Lorem Ipsum is simply dummy text of the printing and typesetting industry.',
                url: null,
                images: [],
                mediaType: {
                    name: `MediaType.${MediaTypeEnum.BLUESKY}`,
                    id: MediaTypeEnum.BLUESKY,
                    icon: mediaTypeIconMap.get(MediaTypeEnum.BLUESKY),
                    socialIconImageUrl: socialMediaTypeImageMap.get(MediaTypeEnum.BLUESKY),
                    isSocial: true,
                    isSavedSearch: false,
                    noHeadlineMessage: mediaTypeNoHeadlineMap.get(MediaTypeEnum.BLUESKY),
                    isDisabled: false,
                    tooltip: `MediaType.${MediaTypeEnum.BLUESKY}`
                },
                sentimentName: (Math.random() - 0.5) * 2 > 0 ? 'Sentiment.POSITIVE' : 'Sentiment.NEGATIVE',
                audienceReach: Math.round(Math.random() * 1000000),
                tvEyesPodcastID: null,
                thumbnailUrl: null,
                sharingInformation: null,
                tiktokData: null,
                avatarPlaceholderText: 'H',
                handle: '@Handle7',
                socialAvatarUrl: null,
            },
        ];

        stubSeries.mentionProvider = new BehaviorSubject(studData);
        return stubSeries;
    }
}
